import React, { useState } from 'react';
import {
  describeRule, occursOn, todayStr, addDays, fmtDay, fmtDuration, uid, PRIORITIES,
} from '../myDayUtils';

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const BLANK = { title: '', type: 'daily', days: [1], n: 2, dayOfMonth: 1, priority: 3, category: '', estimateMin: '', startTime: '' };

/**
 * Recurring activities. A routine is a rule, not a task — the planner
 * materialises one real task per due day, so ticking Monday's version never
 * touches Tuesday's.
 */
export default function RoutinesView({ routines, cats, today, onChange }) {
  const [form, setForm] = useState(BLANK);
  const set = patch => setForm(f => ({ ...f, ...patch }));

  const ruleFrom = f => {
    switch (f.type) {
      case 'weekly':  return { type: 'weekly', days: f.days.length ? [...f.days].sort() : [1] };
      case 'weekdays': return { type: 'weekdays' };
      case 'monthly': return { type: 'monthly', dayOfMonth: Math.min(28, Math.max(1, Number(f.dayOfMonth) || 1)) };
      case 'everyN':  return { type: 'everyN', n: Math.max(1, Number(f.n) || 2), startDate: todayStr() };
      default:        return { type: 'daily' };
    }
  };

  const add = e => {
    e.preventDefault();
    const title = form.title.trim();
    if (!title) return;
    onChange([...routines, {
      id: uid(),
      title,
      rule: ruleFrom(form),
      priority: Number(form.priority) || 3,
      category: form.category || null,
      estimateMin: form.estimateMin ? Number(form.estimateMin) : null,
      startTime: form.startTime || null,
      active: true,
      createdAt: Date.now(),
    }]);
    setForm(BLANK);
  };

  const patch = (id, p) => onChange(routines.map(r => (r.id === id ? { ...r, ...p } : r)));
  const remove = r => {
    if (!window.confirm(`Delete the routine "${r.title}"? Tasks it already created stay on your days.`)) return;
    onChange(routines.filter(x => x.id !== r.id));
  };

  const nextRun = rule => {
    for (let i = 0; i < 400; i++) {
      const d = addDays(today, i);
      if (occursOn(rule, d)) return i === 0 ? 'today' : fmtDay(d);
    }
    return '—';
  };

  return (
    <div className="md-routines">
      <p className="md-lead">
        The parts of the day that always come back. Each due day gets its own task on your
        list, and pausing a routine stops new ones without touching what's already there.
      </p>

      <form className="md-card md-routines__form" onSubmit={add}>
        <h3 className="md-card__title">New routine</h3>
        <label className="md-field">
          <span className="md-field__lbl">What repeats</span>
          <input className="md-input" value={form.title} placeholder="Count the till and log the float"
            onChange={e => set({ title: e.target.value })} />
        </label>

        <div className="md-field">
          <span className="md-field__lbl">How often</span>
          <div className="md-seg md-seg--wrap">
            {[['daily', 'Every day'], ['weekdays', 'Weekdays'], ['weekly', 'Certain days'], ['everyN', 'Every N days'], ['monthly', 'Monthly']].map(([v, l]) => (
              <button key={v} type="button" className={`md-seg__btn${form.type === v ? ' md-seg__btn--on' : ''}`}
                onClick={() => set({ type: v })}>{l}</button>
            ))}
          </div>
        </div>

        {form.type === 'weekly' && (
          <div className="md-habits__dows">
            {DOW.map((d, i) => (
              <button key={i} type="button" className={`md-dow${form.days.includes(i) ? ' md-dow--on' : ''}`}
                onClick={() => set({ days: form.days.includes(i) ? form.days.filter(x => x !== i) : [...form.days, i] })}>
                {d}
              </button>
            ))}
          </div>
        )}
        {form.type === 'everyN' && (
          <label className="md-field">
            <span className="md-field__lbl">Repeat every … days</span>
            <input className="md-input md-input--sm" type="number" min="1" max="365" value={form.n}
              onChange={e => set({ n: e.target.value })} />
          </label>
        )}
        {form.type === 'monthly' && (
          <label className="md-field">
            <span className="md-field__lbl">Day of the month</span>
            <input className="md-input md-input--sm" type="number" min="1" max="28" value={form.dayOfMonth}
              onChange={e => set({ dayOfMonth: e.target.value })} />
          </label>
        )}

        <div className="md-field md-field--row">
          <label className="md-field md-field--grow">
            <span className="md-field__lbl">Priority</span>
            <select className="md-input" value={form.priority} onChange={e => set({ priority: e.target.value })}>
              {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </label>
          <label className="md-field md-field--grow">
            <span className="md-field__lbl">Category</span>
            <select className="md-input" value={form.category} onChange={e => set({ category: e.target.value })}>
              <option value="">None</option>
              {cats.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
            </select>
          </label>
        </div>

        <div className="md-field md-field--row">
          <label className="md-field md-field--grow">
            <span className="md-field__lbl">Start time (optional)</span>
            <input className="md-input" type="time" value={form.startTime} onChange={e => set({ startTime: e.target.value })} />
          </label>
          <label className="md-field md-field--grow">
            <span className="md-field__lbl">Estimate in minutes (optional)</span>
            <input className="md-input" type="number" min="5" step="5" value={form.estimateMin}
              placeholder="30" onChange={e => set({ estimateMin: e.target.value })} />
          </label>
        </div>

        <button className="md-btn md-btn--primary" type="submit" disabled={!form.title.trim()}>Add routine</button>
      </form>

      {routines.length === 0 ? (
        <p className="md-empty">No routines yet. Anything you find yourself re-typing every morning belongs here.</p>
      ) : (
        <ul className="md-routines__list">
          {routines.map(r => (
            <li key={r.id} className={`md-routine${r.active === false ? ' md-routine--off' : ''}`}>
              <div className="md-routine__main">
                <span className="md-routine__title">🔁 {r.title}</span>
                <span className="md-routine__meta">
                  {describeRule(r.rule)}
                  {r.startTime ? ` · ${r.startTime}` : ''}
                  {r.estimateMin ? ` · ${fmtDuration(r.estimateMin)}` : ''}
                  {r.active === false ? ' · paused' : ` · next ${nextRun(r.rule)}`}
                </span>
              </div>
              <button className="md-btn md-btn--sm" onClick={() => patch(r.id, { active: r.active === false })}>
                {r.active === false ? '▶ Resume' : '⏸ Pause'}
              </button>
              <button className="md-iconbtn md-iconbtn--danger" onClick={() => remove(r)} aria-label={`Delete ${r.title}`}>✕</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
